import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Card,
  Tag,
  Popconfirm,
  Typography,
  Switch,
  InputNumber,
} from "antd";
import { Plus, RefreshCw, Edit, Trash2, Search } from "lucide-react";
import ErrorBoundary from "../common/ErrorBoundary";
import "../AccountConfig/AccountConfig.css";

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

/**
 * 通用主数据 CRUD（业务线 / 策略类型），交互与账号配置页风格一致。
 * @param {{ title: string, subtitle?: string, singularName: string, api: object, TitleIcon: React.ComponentType }} props
 */
const MasterDataCrudPage = ({ title, subtitle, singularName, api, TitleIcon }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [draftName, setDraftName] = useState("");
  const [draftStatus, setDraftStatus] = useState(undefined);
  const [appliedName, setAppliedName] = useState("");
  const [appliedStatus, setAppliedStatus] = useState(undefined);

  const [form] = Form.useForm();

  const fetchRows = useCallback(
    async (page, pageSize) => {
      setLoading(true);
      try {
        const params = {
          page,
          page_size: pageSize,
        };
        if (appliedName.trim()) params.name = appliedName.trim();
        if (appliedStatus !== undefined && appliedStatus !== "") {
          params.is_active = appliedStatus === "true";
        }
        const response = await api.list(params);
        const data = response.data?.data;
        setRows(data?.list || []);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: data?.total ?? 0,
        }));
      } catch (err) {
        message.error(
          err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            `加载${singularName}失败`,
        );
      } finally {
        setLoading(false);
      }
    },
    [api, appliedName, appliedStatus, singularName],
  );

  useEffect(() => {
    fetchRows(pagination.current, pagination.pageSize);
  }, [fetchRows, pagination.current, pagination.pageSize, appliedName, appliedStatus]);

  const loadRows = useCallback(() => {
    fetchRows(pagination.current, pagination.pageSize);
  }, [fetchRows, pagination.current, pagination.pageSize]);

  const openModal = (row = null) => {
    if (row) {
      setCurrentRow(row);
      form.setFieldsValue({
        name: row.name,
        code: row.code || "",
        description: row.description || "",
        is_active: row.is_active,
        sort_order: row.sort_order ?? 0,
      });
    } else {
      setCurrentRow(null);
      form.resetFields();
      form.setFieldsValue({ is_active: true, sort_order: 0 });
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name?.trim(),
        code: (values.code || "").trim(),
        description: values.description || "",
        is_active: values.is_active,
        sort_order: Number(values.sort_order) || 0,
      };
      if (currentRow) {
        await api.update(currentRow.id, payload);
        message.success(`${singularName}已更新`);
      } else {
        await api.create(payload);
        message.success(`${singularName}已创建`);
      }
      setShowModal(false);
      form.resetFields();
      setCurrentRow(null);
      loadRows();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message,
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(id);
      message.success(`已删除${singularName}`);
      loadRows();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message,
      );
    }
  };

  const handleTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  const handleSearch = () => {
    setAppliedName(draftName);
    setAppliedStatus(draftStatus);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const columns = [
    {
      title: "名称",
      dataIndex: "name",
      key: "name",
      width: 160,
      ellipsis: true,
    },
    {
      title: "编码",
      dataIndex: "code",
      key: "code",
      width: 120,
      ellipsis: true,
      render: (c) => c || "—",
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (t) => t || "—",
    },
    {
      title: "排序",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 72,
    },
    {
      title: "状态",
      dataIndex: "is_active",
      key: "is_active",
      width: 88,
      render: (v) => (
        <Tag className={`status-tag ${v ? "active" : "inactive"}`}>
          {v ? "启用" : "禁用"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<Edit size={14} />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title={`确定删除该${singularName}？`}
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<Trash2 size={14} />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const Icon = TitleIcon || Plus;

  return (
    <ErrorBoundary>
      <div className="account-config-page">
        <div className="page-header">
          <div className="header-content">
            <Title level={2} className="page-title">
              <Icon className="title-icon" size={32} />
              {title}
            </Title>
            {subtitle ? (
              <Text className="page-subtitle">{subtitle}</Text>
            ) : null}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<Plus size={18} />}
            onClick={() => openModal()}
            className="create-button"
          >
            新增
          </Button>
        </div>

        <Card className="config-list-card">
          <div className="filter-toolbar">
            <Space size="middle" wrap>
              <Input
                placeholder="按名称搜索"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onPressEnter={handleSearch}
                prefix={<Search size={16} />}
                style={{ width: 220 }}
                allowClear
              />
              <Select
                placeholder="状态"
                value={draftStatus}
                onChange={setDraftStatus}
                style={{ width: 120 }}
                allowClear
              >
                <Option value="true">启用</Option>
                <Option value="false">禁用</Option>
              </Select>
              <Button type="primary" onClick={handleSearch} icon={<Search size={16} />}>
                搜索
              </Button>
              <Button icon={<RefreshCw size={16} />} onClick={loadRows}>
                刷新
              </Button>
            </Space>
          </div>
          <Table
            columns={columns}
            dataSource={rows}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              pageSizeOptions: ["10", "20", "50"],
            }}
            onChange={handleTableChange}
            scroll={{ x: "max-content" }}
            className="configs-table"
          />
        </Card>

        <Modal
          title={currentRow ? `编辑${singularName}` : `新增${singularName}`}
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            form.resetFields();
            setCurrentRow(null);
          }}
          footer={null}
          width={520}
          className="config-modal"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ is_active: true, sort_order: 0 }}
            className="config-form"
          >
            <Form.Item
              label="名称"
              name="name"
              rules={[{ required: true, message: "请输入名称" }]}
            >
              <Input placeholder="名称" size="large" />
            </Form.Item>
            <Form.Item label="编码" name="code">
              <Input placeholder="可选，内部编码" size="large" />
            </Form.Item>
            <Form.Item label="描述" name="description">
              <TextArea rows={3} placeholder="描述（可选）" />
            </Form.Item>
            <Form.Item label="排序" name="sort_order">
              <InputNumber min={0} style={{ width: "100%" }} size="large" />
            </Form.Item>
            <Form.Item label="状态" name="is_active" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
            <Form.Item className="form-actions">
              <Space>
                <Button
                  onClick={() => {
                    setShowModal(false);
                    form.resetFields();
                    setCurrentRow(null);
                  }}
                >
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  {currentRow ? "保存" : "创建"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default MasterDataCrudPage;
